import React, { useEffect, useState } from 'react';
import { Loader2, Volume2 } from 'lucide-react';
import { appelAuthentifie } from '../../services/api/assistants';
import { buildApiUrl } from '../../utils/apiUrl';

/**
 * Lecteur de l'enregistrement d'une réunion, pour relire en écoutant.
 *
 * La transcription s'affichait seule : sur un passage douteux, un nom propre
 * déformé ou un chiffre, rien ne permettait de remonter au son. C'est
 * précisément ce que la relecture demande, et c'est ce que les agents d'EG
 * ont signalé.
 *
 * L'enregistrement est derrière le jeton comme le reste : on ne peut pas le
 * poser dans un « src », il faut le chercher puis le tenir en mémoire le
 * temps de l'écoute. Il n'est pas toujours là, et c'est voulu : il est effacé
 * au versement du compte rendu. On le dit alors, plutôt que d'afficher un
 * lecteur muet.
 */
interface Props {
  reunionId: number;
}

const EcouteReunion: React.FC<Props> = ({ reunionId }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [etat, setEtat] = useState<'attente' | 'pret' | 'absent'>('attente');
  const [motif, setMotif] = useState('');

  useEffect(() => {
    let annule = false;
    let objet: string | null = null;

    (async () => {
      try {
        const reponse = await appelAuthentifie(
          buildApiUrl(`/documents/reunions/${reunionId}/enregistrement/`),
        );
        if (!reponse.ok) {
          const corps = await reponse.json().catch(() => ({}));
          if (annule) return;
          setMotif(corps.detail || "L'enregistrement n'est pas disponible.");
          setEtat('absent');
          return;
        }
        const blob = await reponse.blob();
        if (annule) return;
        objet = URL.createObjectURL(blob);
        setUrl(objet);
        setEtat('pret');
      } catch {
        if (annule) return;
        setMotif("L'enregistrement n'a pas pu être chargé.");
        setEtat('absent');
      }
    })();

    return () => {
      annule = true;
      if (objet) URL.revokeObjectURL(objet);
    };
  }, [reunionId]);

  if (etat === 'attente') {
    return (
      <p className="mt-3 flex items-center gap-2 text-xs text-eg-estompe">
        <Loader2 className="w-3 h-3 animate-spin" />
        Chargement de l'enregistrement...
      </p>
    );
  }

  if (etat === 'absent') {
    return <p className="mt-3 text-xs text-eg-estompe">{motif}</p>;
  }

  return (
    <div className="mt-3">
      <p className="mb-2 flex items-center gap-2 text-xs text-eg-gris">
        <Volume2 className="w-3 h-3" />
        Enregistrement de la séance
      </p>
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <audio controls src={url || undefined} className="w-full" />
    </div>
  );
};

export default EcouteReunion;
